import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import AchievementHud from './AchievementHud';
import KeyAreaTabs from './KeyAreaTabs';
import KeyAreaTargetsPanel from './KeyAreaTargetsPanel';
import ImpactMap from './ImpactMap';
import CountryImpactDrawer from './CountryImpactDrawer';
import {
  fetchCountryDrawer,
  fetchKeyAreaTargets,
  fetchTargetsMap,
  fetchTargetsSummary,
  loadMelBoundaries,
} from './api';
import type {
  CountryDrawerData,
  KeyAreaTargetsResponse,
  TargetsMapData,
  TargetsSummary,
} from './types';

const ImpactHero: React.FC = () => {
  const [summary, setSummary] = useState<TargetsSummary | null>(null);
  const [mapData, setMapData] = useState<TargetsMapData | null>(null);
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedFocusId, setSelectedFocusId] = useState<number | null>(null);
  const [selectedKeyAreaId, setSelectedKeyAreaId] = useState<number | null>(null);
  const [keyAreaPanel, setKeyAreaPanel] = useState<KeyAreaTargetsResponse | null>(null);
  const [keyAreaPanelLoading, setKeyAreaPanelLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerData, setDrawerData] = useState<CountryDrawerData | null>(null);
  const [selectedIso3, setSelectedIso3] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const s = await fetchTargetsSummary();
        if (cancelled) return;
        setSummary(s);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load programme progress');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await fetchTargetsMap({
          key_area_id: selectedKeyAreaId,
          focus_objective_id: selectedFocusId,
        });
        if (cancelled) return;
        setMapData(m);

        const iso3s = (m.membership || []).map((n) => String(n.iso3).toUpperCase());
        const geo = await loadMelBoundaries(iso3s);
        if (!cancelled && geo) setGeojson(geo as GeoJSON.FeatureCollection);
      } catch (e: any) {
        if (!cancelled) console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFocusId, selectedKeyAreaId]);

  useEffect(() => {
    let cancelled = false;
    if (selectedKeyAreaId == null) {
      setKeyAreaPanel(null);
      return;
    }
    (async () => {
      try {
        setKeyAreaPanelLoading(true);
        const data = await fetchKeyAreaTargets(selectedKeyAreaId);
        if (!cancelled) setKeyAreaPanel(data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setKeyAreaPanel(null);
      } finally {
        if (!cancelled) setKeyAreaPanelLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedKeyAreaId]);

  const onSelectFocus = useCallback((id: number | null) => {
    setSelectedFocusId(id);
    setSelectedKeyAreaId(null);
  }, []);

  const onSelectKeyArea = useCallback((id: number | null) => {
    setSelectedKeyAreaId(id);
  }, []);

  const onSelectCountry = useCallback(
    async (iso3: string) => {
      setSelectedIso3(iso3);
      setDrawerOpen(true);
      setDrawerLoading(true);
      try {
        const data = await fetchCountryDrawer(iso3, selectedKeyAreaId);
        setDrawerData(data);
      } catch (e: any) {
        setDrawerData({
          country: { iso3, name: iso3, is_member: false },
          activities: [],
          linked_targets: [],
          key_area_id: selectedKeyAreaId,
          empty: true,
          empty_message: e?.message || 'Could not load country details.',
        });
      } finally {
        setDrawerLoading(false);
      }
    },
    [selectedKeyAreaId]
  );

  // Refresh open country drawer when key area filter changes
  useEffect(() => {
    if (!drawerOpen || !selectedIso3) return;
    let cancelled = false;
    (async () => {
      setDrawerLoading(true);
      try {
        const data = await fetchCountryDrawer(selectedIso3, selectedKeyAreaId);
        if (!cancelled) setDrawerData(data);
      } catch {
        /* keep previous */
      } finally {
        if (!cancelled) setDrawerLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedKeyAreaId, drawerOpen, selectedIso3]);

  const legend = useMemo(
    () => (
      <div className="flex flex-wrap gap-3 text-[11px] text-gray-600">
        {selectedKeyAreaId != null ? (
          <>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-[#15736d]" /> Country with activity in this key
              area
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-[#e8eeec] border border-gray-300" /> No activity
              in this key area
            </span>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-[#0f5b57]" /> Member nation
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-[#9ec5c0]" /> Neighbouring country
            </span>
          </>
        )}
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-[#eab308] border border-[#a16207]" /> Activity
          marker
        </span>
      </div>
    ),
    [selectedKeyAreaId]
  );

  const showPanel = selectedKeyAreaId != null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full"
        >
          <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#15736d]">
            Move FAST strategy
          </p>
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">
              EuFMD&apos;s Programme
            </h1>
            <span className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-[#0f5b57] leading-none">
              2023–2027
            </span>
          </div>
          <p className="text-sm sm:text-base text-gray-700 mt-3 leading-relaxed w-full">
            The European Commission for the Control of Foot-and-Mouth Disease is committed to
            counter the threat of Foot-and-mouth and Similar Transboundary Animal Diseases (FAST).
            Our vision is to ensure a safer Europe from FAST diseases, maintaining, rapidly
            regaining, or achieving freedom from these diseases in Member Nations.
          </p>
          <p className="text-sm sm:text-base text-gray-600 mt-2 leading-relaxed w-full">
            Move FAST achieves this through three Focus Objectives — Protect, Respond, and Control —
            supported by seven key Action Areas that strengthen preparedness, response, and disease
            control. Explore progress on the map and by key area below.
          </p>
        </motion.div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 text-sm px-4 py-3">
          {error}
        </div>
      )}

      {loading && !summary && (
        <div className="h-36 rounded-2xl bg-gray-100 animate-pulse" />
      )}

      {summary && (
        <AchievementHud
          overallPct={summary.overall_progress_pct}
          targetCount={summary.target_count}
          targetsWithData={summary.targets_with_data}
          focusObjectives={summary.focus_objectives}
          selectedFocusId={selectedFocusId}
          onSelectFocus={onSelectFocus}
        />
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-3 sm:p-4 shadow-sm space-y-3">
        {summary && (
          <KeyAreaTabs
            keyAreas={summary.key_areas}
            selectedKeyAreaId={selectedKeyAreaId}
            focusFilterId={selectedFocusId}
            onSelect={onSelectKeyArea}
          />
        )}
        {legend}

        <div className={`flex flex-col gap-3 ${showPanel ? 'lg:flex-row lg:items-stretch' : ''}`}>
          <AnimatePresence>
            {showPanel && (
              <KeyAreaTargetsPanel
                data={keyAreaPanel}
                loading={keyAreaPanelLoading}
                onClose={() => setSelectedKeyAreaId(null)}
              />
            )}
          </AnimatePresence>

          <div className="min-w-0 flex-1">
            <ImpactMap
              geojson={geojson}
              membership={mapData?.membership || []}
              evidenceByCountry={mapData?.evidence_by_country || []}
              selectedIso3={selectedIso3}
              activityFilterActive={selectedKeyAreaId != null}
              layoutToken={showPanel ? 'panel' : 'full'}
              onSelectCountry={onSelectCountry}
            />
          </div>
        </div>

        {!geojson && mapData && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
            Map tiles should appear above; country outlines are still loading from UN GeoServices…
          </p>
        )}
      </div>

      <CountryImpactDrawer
        open={drawerOpen}
        data={drawerData}
        loading={drawerLoading}
        keyAreaLabel={
          selectedKeyAreaId != null
            ? keyAreaPanel?.key_area?.short_label ||
              summary?.key_areas.find((k) => k.id === selectedKeyAreaId)?.short_label
            : null
        }
        onClose={() => {
          setDrawerOpen(false);
          setSelectedIso3(null);
        }}
      />
    </div>
  );
};

export default ImpactHero;
